import * as Y from "yjs";
import {uploadFile} from "../../components/uploader/Provider";
import {VisionBoardBag} from "./canvas-utils";
import {createImageNode} from "./canvas-node-utils";

const getBlob = (canvas: HTMLCanvasElement, type: string) => {
  return new Promise<Blob | null>((res) => {
    canvas.toBlob((blob) => {
      if (blob) {
        res(blob);
      } else {
        res(null);
      }
    }, type);
  });
};

const replaceExtension = (filename: string, ext: string) => {
  const lastDotIndex = filename.lastIndexOf(".");
  const baseName = lastDotIndex === -1 ? filename : filename.slice(0, lastDotIndex);
  return baseName.slice(0, 32) + "." + ext;
};

const getBestBlob = async (canvas: HTMLCanvasElement, fileName: string) => {
  const types = ["image/jpeg", "image/png", "image/webp"];
  let best = null as null | [Blob, number, string];
  for (const t of types) {
    const blob = await getBlob(canvas, t);
    if (!blob) continue;
    if (!best || blob.size < best[0].size) {
      best = [blob, blob.size, t];
    }
  }
  const extension = best![2].split("/")[1];
  const type = best![2];
  const file = new File([best![0]], replaceExtension(fileName, extension), {type});
  return {file, type};
};

const MAX_DIMENSION = 1200;

const resizeImage = async (imgNode: HTMLImageElement, fileName: string) => {
  const canvas = document.createElement("canvas");
  const [orgW, orgH] = [imgNode.naturalWidth, imgNode.naturalHeight];
  const getDims = () => {
    if (orgW > orgH) {
      const D = Math.min(MAX_DIMENSION, orgW);
      return [D, Math.round(D * (orgH / orgW))];
    } else {
      const D = Math.min(MAX_DIMENSION, orgH);
      return [Math.round(D * (orgW / orgH)), D];
    }
  };
  const [w, h] = getDims();
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(imgNode, 0, 0, w, h);
  const {file} = await getBestBlob(canvas, fileName);
  return {file, dims: [w, h]};
};

const checkIfImage = (file: File) =>
  new Promise<HTMLImageElement | null>((resolve, reject) => {
    // Check MIME type first
    if (!file.type.startsWith("image/")) {
      resolve(null);
      return;
    }

    // Double check by creating an image object
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(null);
    };

    img.src = objectUrl;
  });

type Res =
  | {
      ok: true;
    }
  | {ok: false; message: string};

export const handleCanvasFileDrop = async (passedFile: File, bag: VisionBoardBag): Promise<Res> => {
  const {store} = bag;
  const img = await checkIfImage(passedFile);
  if (!img) return {ok: false, message: "not an image"};
  const getResizedImage = async () => {
    const resizeInfo = await resizeImage(img, passedFile.name);
    if (resizeInfo.file.size > passedFile.size) {
      return {file: passedFile, dims: [img.naturalWidth, img.naturalHeight]};
    } else {
      return resizeInfo;
    }
  };
  const {
    file,
    dims: [w, h],
  } = await getResizedImage();
  const {node: nodeMap, id} = createImageNode(bag, [w, h]);
  bag.selectionStore.setNodeIds([id]);
  const res = (await uploadFile({
    id: `canvas-${id}`,
    affectsQuota: false,
    file,
    noAccount: false,
    onUpload: () => null,
  }).catch((e) => {
    (nodeMap.parent as Y.Map<any>).delete(id);
    return Promise.reject(res);
  })) as {url: string};
  store.doc.transact(() => {
    nodeMap.set("value", {
      url: res.url,
      width: w,
      height: h,
    });
  }, "$server");
  return {ok: true};
};
